import { format, parseISO } from 'date-fns';
import { useState } from 'react';
import { toast } from 'sonner';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { SlackNotificationChannelField } from '@/modules/slack-notification-channel/components/SlackNotificationChannelField';
import { Badge } from '@/modules/ui/components/badge/Badge';
import { Button } from '@/modules/ui/components/button/Button';
import { Card, CardContent } from '@/modules/ui/components/card/Card';
import { ConfirmDeleteDialog } from '@/modules/ui/components/confirm-delete-dialog/ConfirmDeleteDialog';
import { Paragraph } from '@/modules/ui/components/paragraph/Paragraph';
import { Subheading } from '@/modules/ui/components/subheading/Subheading';
import { LinkedInLimits } from '@/modules/user-external-accounts/components/LinkedInLimits';
import { UserExternalAccountAvatar } from '@/modules/user-external-accounts/components/UserExternalAccountAvatar';
import { useDeleteUserExternalAccountMutation } from '@/modules/user-external-accounts/mutations/useDeleteUserExternalAccountMutation';
import type { UserExternalAccountApi } from '@/modules/user-external-accounts/types';
import { extractErrorMessage } from '@/utils/extractErrorMessage';

interface UserExternalAccountCardProps {
  externalAccount: UserExternalAccountApi;
  userId: string;
}

export const UserExternalAccountCard = ({
  externalAccount,
  userId,
}: UserExternalAccountCardProps) => {
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const deleteExternalAccountMutation = useDeleteUserExternalAccountMutation();

  const isSlackAccount = externalAccount.type === 'Slack';
  const isLinkedInAccount = externalAccount.type === 'LinkedIn';
  const slackTeamName = externalAccount.teamName?.trim();

  const displayTitle =
    isSlackAccount && slackTeamName ? slackTeamName : externalAccount.type;
  const secondaryLabel =
    isSlackAccount && slackTeamName ? externalAccount.type : undefined;

  const handleDelete = () => {
    deleteExternalAccountMutation.mutate(
      { userId, id: externalAccount.id },
      {
        onSuccess: () => {
          toast.success('External account has been deleted');
          setIsDeleteDialogOpen(false);
        },
        onError: (error) => {
          toast.error(
            `External account has not been deleted: ${extractErrorMessage(error)}`,
          );
        },
      },
    );
  };

  return (
    <Card>
      <CardContent>
        <div
          className={`flex gap-4 ${isLinkedInAccount ? 'items-start' : 'items-center'}`}
        >
          <UserExternalAccountAvatar type={externalAccount.type} />

          <div className="flex flex-1 flex-col">
            <div className="flex items-center gap-2">
              <Subheading>{displayTitle}</Subheading>
              <Badge
                variant={
                  externalAccount.status === 'Active' ? 'default' : 'secondary'
                }
                className={
                  externalAccount.status === 'Active'
                    ? 'border-green-200 bg-green-100 text-green-800'
                    : 'border-orange-200 bg-orange-100 text-orange-800'
                }
                size="sm"
              >
                {externalAccount.status}
              </Badge>
            </div>

            {secondaryLabel && (
              <Paragraph variant="caption">{secondaryLabel}</Paragraph>
            )}

            <Paragraph variant="caption">
              Connected:{' '}
              {externalAccount.createdAt
                ? format(parseISO(externalAccount.createdAt), 'MMMM d, yyyy')
                : '-'}
            </Paragraph>

            {isLinkedInAccount && (
              <LinkedInLimits linkedInAccountId={externalAccount.id} />
            )}
          </div>

          <PermissionsGate
            permissions={[permissions.UserExternalAccounts.Write]}
          >
            <Button
              variant="outline"
              className="border-destructive text-destructive hover:bg-destructive/5 hover:text-destructive"
              onClick={() => setIsDeleteDialogOpen(true)}
            >
              Delete
            </Button>
          </PermissionsGate>
        </div>

        {isSlackAccount && externalAccount.status === 'Active' && (
          <div className="mt-4 border-t pt-4">
            <SlackNotificationChannelField />
          </div>
        )}
      </CardContent>

      <ConfirmDeleteDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        title="Delete external account?"
        description="Are you sure you want to delete this external account?"
        onDelete={handleDelete}
        onCancel={() => setIsDeleteDialogOpen(false)}
      />
    </Card>
  );
};
